import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import {
  bypassesPageAccess,
  getDefaultLandingPath,
  isPathAllowedForUser,
} from '../../navigation/pageAccess';

interface PageAccessGuardProps {
  children: React.ReactNode;
}

/**
 * Restricts content inside AppShell to routes allowed for the user's role.
 * Delivery users and staff/superusers bypass checks.
 */
export const PageAccessGuard: React.FC<PageAccessGuardProps> = ({ children }) => {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const pathname = location.pathname;

  if (!user) {
    return <>{children}</>;
  }

  if (isPathAllowedForUser(pathname, user)) {
    return <>{children}</>;
  }

  const target = getDefaultLandingPath(user);

  if (target !== pathname && isPathAllowedForUser(target, user)) {
    return <Navigate to={target} replace />;
  }

  if (!bypassesPageAccess(user) && (user.visible_page_paths?.length ?? 0) === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-gray-800">
        <p className="font-medium">No pages assigned</p>
        <p className="mt-2 text-sm text-gray-600">
          Your role does not include any app pages. Contact an administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-gray-800">
      <p className="font-medium">Page not available</p>
      <p className="mt-2 text-sm text-gray-600">
        You do not have access to this area. Ask an administrator to enable it for your role.
      </p>
    </div>
  );
};

PageAccessGuard.displayName = 'PageAccessGuard';
